/**
 * Button — the app's single button component. Every clickable action in
 * the app (including ones that used to be styled `<a>` links in the old
 * build, which is what caused the stray underline the user reported —
 * text-decoration on an <a> plus no `role="button"` reset) renders through
 * this, so hover/press/focus/disabled states are consistent everywhere.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

const buttonVariants = cva(
  "inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md text-md font-medium " +
    "transition-all duration-150 ease-premium disabled:pointer-events-none disabled:opacity-50 " +
    "active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary: "bg-neutral-900 text-white shadow-sm hover:bg-neutral-800",
        accent:
          "bg-accent-600 text-white shadow-sm hover:bg-accent-700 " +
          "focus-visible:ring-2 focus-visible:ring-accent-500/40",
        secondary:
          "bg-white text-neutral-700 shadow-xs ring-1 ring-inset ring-neutral-200 hover:bg-neutral-50 hover:text-neutral-900",
        ghost: "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900",
        danger:
          "bg-white text-danger-600 shadow-xs ring-1 ring-inset ring-danger-100 hover:bg-danger-50",
        "danger-solid": "bg-danger-600 text-white shadow-sm hover:bg-danger-700",
        link: "text-accent-600 underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-3 text-base",
        md: "h-9 px-4",
        lg: "h-11 px-6 text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, loading, disabled, children, ...props }, ref) => {
    // `asChild` renders Radix `Slot`, which requires EXACTLY one React
    // element child (it clones its props onto that one child, typically a
    // <Link>) — so the loading-spinner span can't be injected alongside
    // `children` here the way the plain-<button> path does below, or Slot
    // throws "expected a single React element child". asChild buttons are
    // navigation links in this app and never show the loading state.
    if (asChild) {
      return (
        <Slot ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="size-4 animate-spin" />}
        {children}
      </button>
    );
  }
);
Button.displayName = "Button";
